import React from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Row, Col, Typography, Space, Popover, Form } from 'antd';
import { useTranslation } from 'react-i18next';

import KeywordSearchInput from '@components/shared/ui/KeywordSearchInput';

const { Text } = Typography;

/**
 * 從 searchable prop 解析設定。
 *
 * searchable 必須是物件 { match, maxCount, placeholder, tooltipContent }。
 *   match: 'prefix'（預設）/ 'contains'
 *   maxCount: 預設 5
 *
 * 回傳 null 表示不顯示搜尋框。
 */
function resolveSearchable(searchable) {
  if (!searchable) return null;
  if (typeof searchable !== 'object') {
    // 不再支援 boolean 形式；若收到舊 prop 直接退回 prefix 預設
    return { match: 'prefix', maxCount: 5 };
  }
  return {
    match: searchable.match === 'contains' ? 'contains' : 'prefix',
    maxCount: searchable.maxCount ?? 5,
    placeholder: searchable.placeholder,
    tooltipContent: searchable.tooltipContent,
    // searchFields：可搜尋欄位的「已翻譯」標籤陣列（重用 models.* 標籤），
    // 由 FilterComponent 自動組成「可搜尋欄位：A、B、C」+ 前綴/包含行為說明。
    searchFields: Array.isArray(searchable.searchFields) ? searchable.searchFields.filter(Boolean) : undefined,
  };
}

export default function FilterComponent({ searchTooltipContent, searchable }) {
  const { t } = useTranslation();
  const config = resolveSearchable(searchable);
  if (!config) return null;

  const renderTooltipContent = () => {
    const title = t('common.table.filters.search_keywords_tooltip.title');
    const behavior = config.match === 'contains'
      ? t('common.table.filters.search_keywords_tooltip.contains')
      : t('common.table.filters.search_keywords_tooltip.prefix');
    // 優先序：明確覆寫(searchTooltipContent / tooltipContent) > 由 searchFields 自動組成 > 純行為說明
    let content;
    if (searchTooltipContent) {
      content = searchTooltipContent;
    } else if (config.tooltipContent) {
      content = config.tooltipContent;
    } else if (config.searchFields && config.searchFields.length > 0) {
      const fieldsLine = t('common.table.filters.search_keywords_tooltip.fields', {
        fields: config.searchFields.join('、'),
      });
      content = `${fieldsLine}\n${behavior}`;
    } else {
      content = behavior;
    }

    return (
      <div style={{ maxWidth: 280 }}>
        {title && <Text strong style={{ display: 'block', marginBottom: 8 }}>{title}</Text>}
        {content && <Text style={{ display: 'block', whiteSpace: 'pre-line' }}>{content}</Text>}
      </div>
    );
  };

  const placeholder =
    config.placeholder ||
    t('common.label.keyword_search_placeholder', { count: config.maxCount });

  return (
    <Row gutter={[12, 8]} align="middle" style={{ marginBottom: '8px' }}>
      <Col xs={20} sm={8} md={4}>
        <Space size={8} style={{ width: '100%' }}>
          <Popover
            content={renderTooltipContent()}
            trigger="hover"
            placement="top"
          >
            <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 14, cursor: 'pointer', flexShrink: 0 }} />
          </Popover>
          <Text>{t('common.table.filters.search_keywords')}</Text>
        </Space>
      </Col>
      <Col xs={20} sm={14} md={18}>
        <Form.Item name="search" noStyle>
          <KeywordSearchInput
            placeholder={placeholder}
            maxCount={config.maxCount}
          />
        </Form.Item>
      </Col>
      <Col xs={4} sm={2} md={2}></Col>
    </Row>
  );
}
